import { Button, Chip, ChipConfirmModal, Table, TableBody, TableCell, TableRow } from "@ondit/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Gauge, Quote } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { TaskSummary } from "~/common/tasks/summary";
import { useAccess, whyDisabled } from "~/core/access/index";
import { ApiError, api, type LoopState, type Overview } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { PageHead, Section, Stat } from "~/core/ui/page";
import { Async, Empty } from "~/core/ui/states";
import { Drill } from "~/modules/analytics/components/detail";
import { type FilterState, Filters } from "~/modules/analytics/components/filters";
import { InsightIcon } from "~/modules/analytics/components/insight-icon";
import { TodoList } from "~/modules/analytics/components/todo";
import { magnitudeColor } from "~/modules/analytics/lib/bar-color";
import { LOOP, readingPath } from "~/modules/analytics/lib/loop";

/** A manual run inside this window asks before it spends the credits again. */
const DAY_MS = 24 * 60 * 60 * 1000;

interface Props {
	orgId: string;
	entityId: string;
	filters: FilterState;
	setFilters: (next: Partial<FilterState>) => void;
}

/**
 * The screen the whole product is judged on.
 *
 * It answers three questions in order: where do I stand, did it move, what
 * happens next. The second one is the hard one, and the rule is absolute — a
 * movement is shown only when the server says it is significant, and otherwise
 * it says "no conclusion yet" and explains the threshold, right under the
 * number it refuses to draw an arrow on.
 *
 * That refusal is the product. Every competitor draws an arrow on two samples;
 * the first time a customer checks one of those arrows against reality and finds
 * it was noise, the whole dashboard becomes decoration.
 */
export function OverviewPage({ orgId, entityId, filters, setFilters }: Props) {
	const t = useT();
	const { locale } = useI18n();
	const { editor, why } = useAccess();
	const queryClient = useQueryClient();
	const scope = { range: filters.range, engine: filters.engine, locale: filters.locale };

	const state = useQuery({
		queryKey: [...keys.entity(entityId), "overview", scope],
		queryFn: () => api.overview(entityId, filters),
		...read,
	});

	// Its own request rather than a field on the overview: the answer to "what
	// should I do" is useful even on a brand with nothing measured yet, which is
	// exactly when the overview has no numbers to hang it off.
	const todo = useQuery({
		queryKey: [...keys.entity(entityId), "actions", scope],
		queryFn: () => api.actions(entityId, filters),
		...read,
	});

	const sample = useMutation({
		mutationFn: () => api.sampleNow(entityId, filters),
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});
	const sampling = sample.isPending;
	const firstPassRunning =
		todo.data?.some((action) => action.code === "sampling_now" || action.code === "setup_running") ?? false;

	async function sampleNow() {
		try {
			const run = await sample.mutateAsync();
			toast.info(t("manual.sampleEstimate", { n: String(run.estimate_credits) }));
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		}
	}

	// A run inside the last day is the one a person repeats by accident — the
	// button is the same button, the page looks the same afterwards, and the
	// credits are gone either way. Asked rather than refused: sampling again is
	// the right move after changing a prompt or adding an engine.
	const sampledAt = state.data?.last_sampled_at;
	const repeatWindow = sampledAt !== null && sampledAt !== undefined && Date.now() - Date.parse(sampledAt) < DAY_MS;
	const [confirmRepeat, setConfirmRepeat] = useState(false);

	function requestSample() {
		if (repeatWindow) setConfirmRepeat(true);
		else void sampleNow();
	}

	return (
		<>
			<PageHead
				icon={<Gauge />}
				title={t("overview.title")}
				filters={<Filters segments={["range", "locale"]} value={filters} onChange={setFilters} />}
				actions={
					// Off while the first pass is in flight. The card below says a run
					// is going; a live "sample now" beside it reads as an invitation
					// to start a second one, and its credits are spent before
					// anything on this screen has a number on it.
					<Chip
						variant="primary"
						type="button"
						onClick={requestSample}
						disabled={sampling || firstPassRunning || !editor}
						title={firstPassRunning ? t("overview.sampleRunning") : whyDisabled(editor, why.editor)}
					>
						{t(sampling ? "state.loading" : "overview.sampleNow")}
					</Chip>
				}
			/>

			<ChipConfirmModal
				open={confirmRepeat}
				onOpenChange={setConfirmRepeat}
				title={t("manual.repeatTitle")}
				text={t("manual.repeatBody", { when: fmt.relative(locale, sampledAt ?? null) ?? "" })}
				dismissLabel={t("common.cancel")}
				confirm={{
					label: t("manual.repeatConfirm"),
					variant: "primary",
					disabled: sampling,
					onClick: () => {
						setConfirmRepeat(false);
						void sampleNow();
					},
				}}
			/>

			<div className="insight-dashboard overview-dashboard">
				{/* Before the to-do list, not after it. This row is where the brand is;
			    the list under it is what to do about that, and an instruction read
			    before its context is an instruction somebody follows without
			    knowing why. */}
				{state.data ? <Loop state={state.data.loop_state} entityId={entityId} /> : null}
				{!state.data?.tracked_questions || state.isError ? <TaskSummary orgId={orgId} brandId={entityId} /> : null}

				{!state.data?.tracked_questions && todo.data && todo.data.length > 0 ? (
					<Section title={t("todo.title")} lede={t("todo.lede")}>
						<TodoList compact entityId={entityId} actions={todo.data} onSample={requestSample} sampling={sampling} />
					</Section>
				) : null}
				<Async state={state}>
					{(data) => {
						const nextSample = data.planned.find((item) => item.kind.startsWith("sample"))?.at ?? null;
						return (
							<>
								{/* `tracked_questions` counts questions the week has *answers*
								    for, so a brand whose first pass is still running reads as
								    zero — and told the person who just wrote eight questions in
								    the wizard to go and add some questions. The card above
								    already says what is happening; nothing is the honest thing
								    to put here until the answers land. The same holds when the
								    first pass *failed*: the to-do list above says so and what to
								    do about it, and "add a question" under it is wrong twice —
								    the brand has questions, and adding another fixes nothing. */}
								{data.tracked_questions === 0 ? (
									firstPassRunning || (todo.data?.length ?? 0) > 0 ? null : (
										<Empty
											title={t("overview.noData")}
											body={t("overview.noDataBody")}
											action={
												<Button asChild variant="primary">
													<Link to={readingPath(`/w/${entityId}`, "prompts")}>{t("questions.add")}</Link>
												</Button>
											}
										/>
									)
								) : (
									<>
										<div className="overview-columns">
											<div className="overview-primary">
												<Section title={t("todo.title")} lede={t("todo.lede")}>
													<Async state={todo}>
														{(actions) => (
															<TodoList
																compact
																entityId={entityId}
																actions={actions}
																onSample={requestSample}
																sampling={sampling}
															/>
														)}
													</Async>
												</Section>
												<TaskSummary orgId={orgId} brandId={entityId} />
											</div>
											<div className="overview-evidence">
												<div className="overview-stats">
													{/* Each card opens onto the rows it counted, at the grain it
								    counted them: the first two count questions in the week
								    the headline is about, the last two count citations over
								    the window in the filter bar. A drawer at the wrong grain
								    would show a different number from the card above it. */}
													<Stat
														label={t("overview.statQuestions")}
														hint={t("hint.trackedQuestions")}
														value={
															<Drill
																view={{
																	kind: "questions",
																	label: t("overview.statQuestions"),
																	filters: { week: "current" },
																}}
															>
																{String(data.tracked_questions)}
															</Drill>
														}
														foot={
															<Drill
																view={{
																	kind: "questions",
																	label: t("overview.statCited", { cited: data.cited_questions }),
																	filters: { week: "current", cited: "yes" },
																}}
															>
																{t("overview.statCited", { cited: data.cited_questions })}
															</Drill>
														}
													/>
													<Stat
														label={t("overview.statOndit")}
														hint={t("hint.onditPages")}
														value={
															data.ondit_pages_cited > 0 ? (
																<Drill
																	view={{
																		kind: "citations",
																		label: t("overview.statOndit"),
																		filters: { source: "ondit" },
																	}}
																>
																	{String(data.ondit_pages_cited)}
																</Drill>
															) : (
																"—"
															)
														}
													/>
													<Stat
														label={t("overview.statOwn")}
														hint={t("hint.ownSite")}
														value={
															data.own_site_cited > 0 ? (
																<Drill
																	view={{ kind: "citations", label: t("overview.statOwn"), filters: { source: "own" } }}
																>
																	{String(data.own_site_cited)}
																</Drill>
															) : (
																"—"
															)
														}
													/>
												</div>
												<Section title={t("overview.competitors")} lede={t("overview.shareLede")}>
													{data.competitors.length === 0 ? (
														<p className="faint">{t("competitors.empty")}</p>
													) : (
														<div className="table-scroll">
															<Table variant="resource">
																<TableBody>
																	{data.competitors.slice(0, 8).map((rival, i) => (
																		<TableRow key={rival.domain}>
																			<TableCell className="wrap">
																				<span className="rank num">{i + 1}</span>
																				{/* A ranking says who. The name opens why them:
																	    their twelve weeks, the pages of theirs the
																	    engines reach for, and the questions they take. */}
																				<Drill
																					view={{
																						kind: "rival",
																						label: rival.name ?? rival.domain,
																						domain: rival.domain,
																					}}
																				>
																					{rival.name ?? rival.domain}
																				</Drill>
																			</TableCell>
																			<TableCell>
																				<span className="bar">
																					<span
																						style={{
																							width: `${Math.min(rival.share, 100)}%`,
																							background: magnitudeColor(rival.share),
																						}}
																					/>
																				</span>
																			</TableCell>
																			<TableCell className="num mono">{fmt.percent(locale, rival.share)}</TableCell>
																		</TableRow>
																	))}
																</TableBody>
															</Table>
														</div>
													)}
												</Section>
											</div>
										</div>
										<div className="overview-share">
											<Section
												title={t("overview.shareTitle")}
												lede={t("overview.shareLede")}
												icon={
													<InsightIcon tone="detect">
														<Quote />
													</InsightIcon>
												}
												action={
													<span className="insight-context">
														{t("overview.lastSampled")} ·{" "}
														{fmt.relative(locale, data.last_sampled_at) ?? t("overview.never")}
														{/* "Last sampled 6 days ago" on its own is the shape of a
												    product that has stopped. The next run is the half of
												    that sentence that says it has not, and it is already
												    in `planned` — ordered by when the worker takes it. */}
														{nextSample
															? ` · ${t("overview.nextSample", { when: fmt.relative(locale, nextSample) ?? "" })}`
															: ""}
													</span>
												}
											>
												<div className={data.trend.length > 1 ? "overview-trend has-trend" : "overview-trend"}>
													<p className="pane-label">{t("overview.trend")}</p>
													{/* The share is an average of the per-engine shares, so
										    what is under it is the questions it averaged over —
										    not a count that has to equal the percentage. */}
													<p className="pane-value">
														<Drill
															view={{
																kind: "questions",
																label: t("overview.shareTitle"),
																filters: { week: "current" },
															}}
														>
															{data.share === null ? "—" : fmt.percent(locale, data.share)}
														</Drill>
													</p>
													<Movement data={data} />
													{data.trend.length > 1 ? (
														<Trend points={[...data.trend].reverse()} locale={locale} />
													) : (
														<p className="faint">{t("overview.noTrend")}</p>
													)}
												</div>
											</Section>
										</div>
									</>
								)}
							</>
						);
					}}
				</Async>
			</div>
		</>
	);
}

/**
 * The loop, as five counts in the order it runs them.
 *
 * The panel's sidebar says the product is five steps; nothing on the screen it
 * opens on said where this brand *was* in them. Five numbers do: answers
 * collected, gaps nobody has acted on, drafts waiting for a person, pages live,
 * conclusions reached. A zero anywhere in the row is the loop stopping there,
 * and the number is the way to the screen that says why.
 *
 * Counts and not a progress bar. A bar implies the five are proportions of one
 * whole, and they are five different things: a brand with 400 answers and 3
 * drafts is not "mostly done".
 */
function Loop({ state, entityId }: { state: LoopState; entityId: string }) {
	const t = useT();
	const base = `/w/${entityId}`;
	const steps: { key: string; value: number; to: string }[] = [
		{ key: "answers", value: state.answers, to: readingPath(base, "visibility") },
		{ key: "gaps", value: state.open_gaps, to: readingPath(base, "gaps") },
		{ key: "waiting", value: state.waiting, to: readingPath(base, "drafts") },
		{ key: "published", value: state.published, to: readingPath(base, "published") },
		{ key: "concluded", value: state.concluded, to: readingPath(base, "since") },
	];

	return (
		<nav className="loop" aria-label={t("loop.title")}>
			{steps.map((step, index) => (
				<Link className={step.value === 0 ? "loop-step is-empty" : "loop-step"} key={step.key} to={step.to}>
					<span className="loop-index num" aria-hidden="true">
						{String(index + 1).padStart(2, "0")}
					</span>
					<span className="loop-n">{t(`nav.stage.${LOOP[index].key}`)}</span>
					<span className="loop-value num">{step.value}</span>
					{/* A zero used to read as "there is nothing here", when what it
					    means is "the loop has not got this far yet". Same span,
					    different sentence: the step says what fills it. */}
					<span className="loop-label">{t(step.value === 0 ? `loop.pending.${step.key}` : `loop.${step.key}`)}</span>
				</Link>
			))}
		</nav>
	);
}

/**
 * The delta, or the refusal to report one.
 *
 * "No conclusion yet" is deliberately not a warning colour. It is an honest
 * answer, and dressing it as a problem would teach people to read honesty as
 * something to fix.
 */
function Movement({ data }: { data: Overview }) {
	const t = useT();
	const { locale } = useI18n();

	if (!data.movement) {
		return (
			<>
				<span className="headline-delta unknown">{t("overview.noConclusion")}</span>
				<p className="headline-sub">{t("overview.noConclusionWhy")}</p>
			</>
		);
	}

	return (
		<span className={data.movement.delta >= 0 ? "headline-delta up" : "headline-delta down"}>
			{data.movement.delta >= 0 ? "▲" : "▼"} {t("overview.delta", { delta: fmt.signed(locale, data.movement.delta) })}
		</span>
	);
}

/**
 * The twelve-week trend.
 *
 * The y-axis always starts at zero and the gridlines are labelled. A truncated
 * axis turns a two-point move into a cliff, and an unlabelled line is a shape
 * rather than a measurement — this is a number customers make decisions on.
 */
function Trend({ points, locale }: { points: { week: string; share: number }[]; locale: "en" | "zh" }) {
	const width = 640;
	const height = 180;
	const pad = { top: 8, right: 8, bottom: 22, left: 34 };
	const plotW = width - pad.left - pad.right;
	const plotH = height - pad.top - pad.bottom;

	const max = Math.max(10, ...points.map((p) => p.share));
	const x = (i: number) => pad.left + (points.length > 1 ? (i * plotW) / (points.length - 1) : plotW / 2);
	const y = (share: number) => pad.top + plotH - (share / max) * plotH;

	const line = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.share).toFixed(1)}`).join(" ");
	const area = `${line} L${x(points.length - 1).toFixed(1)},${(pad.top + plotH).toFixed(1)} L${x(0).toFixed(1)},${(pad.top + plotH).toFixed(1)} Z`;

	return (
		<svg className="spark" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img">
			<title>{points.map((p) => `${p.week}: ${fmt.percent(locale, p.share)}`).join(", ")}</title>

			{[0, max / 2, max].map((tick) => (
				<g key={tick}>
					<line className="spark-grid" x1={pad.left} x2={width - pad.right} y1={y(tick)} y2={y(tick)} />
					<text className="spark-tick" x={pad.left - 6} y={y(tick) + 3} textAnchor="end">
						{Math.round(tick)}%
					</text>
				</g>
			))}

			<path className="spark-area" d={area} />
			<path className="spark-line" d={line} />

			{/* Only the ends are labelled: twelve week numbers along an axis this
			    wide overlap into a grey smear. */}
			{[0, points.length - 1].map((i, n) => (
				<text
					key={points[i].week}
					className="spark-tick"
					x={x(i)}
					y={height - 6}
					textAnchor={n === 0 ? "start" : "end"}
				>
					{points[i].week}
				</text>
			))}
		</svg>
	);
}
